// src/app/components/form-builder/FormSettingsTabs.ApprovalTab.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Full multi-language support:
//   - approval_email_subject / approval_email_body stored as MultiLangValue
//   - rejection_email_subject / rejection_email_body stored as MultiLangValue
//   - Per-template language tab switcher
//   - Missing-translation warning badges
//   - Field labels in VariableChips use activeLang
//   - mlGet/mlSet helpers used throughout
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useCallback } from "react";
import {
  CheckCircle, XCircle, ChevronDown, ChevronUp, Globe,
  Eye, Code, Zap, Check,
} from "lucide-react";
import {
  Form, FormField, MultiLangValue, mlGet, mlSet, mlWrap,
} from "./types";
import { Language, DEFAULT_LANGUAGES } from "@/types/multilang";

// ─── Shared micro-components ──────────────────────────────────────────────────

const Inp = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(
  ({ mono, className, ...p }, ref) => (
    <input ref={ref} {...p} className={`w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50/50 hover:bg-white font-sans focus:bg-white focus:border-[#ed5a29] focus:ring-4 focus:ring-[#ed5a29]/10 outline-none shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)] transition-all duration-200 ${mono ? "font-mono text-xs" : ""} ${className ?? ""}`} />
  )
);
const Lbl: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <label className={`block text-[10px] font-bold text-gray-500 mb-1.5 font-sans uppercase tracking-wider ${className ?? ''}`}>{children}</label>
);
const Fld: React.FC<{ label: string; badge?: React.ReactNode; children: React.ReactNode; className?: string }> = ({ label, badge, children, className }) => (
  <div className={`mb-5 ${className ?? ""}`}>
    <Lbl>{label}{badge}</Lbl>
    {children}
  </div>
);
const Txt = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }>(
  ({ mono, className, ...p }, ref) => (
    <textarea ref={ref} {...p} className={`w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50/50 hover:bg-white font-sans focus:bg-white focus:border-[#ed5a29] focus:ring-4 focus:ring-[#ed5a29]/10 outline-none shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)] transition-all duration-200 resize-none ${mono ? "font-mono text-xs" : ""} ${className ?? ""}`} />
  )
);
const Card: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <div className="bg-white border border-gray-200/80 rounded-2xl p-6 mb-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all duration-300">
    <div className="mb-5">
      <h3 className="text-[15px] font-semibold text-gray-900 font-sans tracking-tight">{title}</h3>
      {sub && <p className="text-[13px] text-gray-500 font-sans mt-1 leading-relaxed">{sub}</p>}
    </div>
    {children}
  </div>
);
const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; sub?: string }> = ({ checked, onChange, label, sub }) => (
  <div className="flex items-center justify-between py-3 cursor-pointer group" onClick={() => onChange(!checked)}>
    <div className="pr-4">
      <p className="text-sm font-semibold text-gray-900 font-sans group-hover:text-black transition-colors">{label}</p>
      {sub && <p className="text-[13px] text-gray-500 font-sans mt-0.5">{sub}</p>}
    </div>
    <button
      type="button"
      className={`relative flex-shrink-0 transition-all duration-300 ease-in-out border border-transparent shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] ${checked ? "bg-[#ed5a29]" : "bg-gray-200 group-hover:bg-gray-300"}`}
      style={{ width: 44, height: 24, borderRadius: 12 }}
    >
      <span className={`absolute top-[1px] w-[20px] h-[20px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${checked ? "left-[21px]" : "left-[1px]"}`} />
    </button>
  </div>
);
const InfoBox: React.FC<{ children: React.ReactNode; type?: "info" | "warn" | "success" }> = ({ children, type = "info" }) => {
  const styles = {
    info: "bg-blue-50/50 border-blue-100 text-blue-700",
    warn: "bg-orange-50/50 border-orange-100 text-orange-700",
    success: "bg-emerald-50/50 border-emerald-100 text-emerald-700",
  };
  return <div className={`border rounded-xl px-4 py-3 text-[13px] leading-relaxed font-sans mb-5 shadow-sm ${styles[type]}`}>{children}</div>;
};
const G2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-2 gap-5">{children}</div>
);

// ─── Lang badge ───────────────────────────────────────────────────────────────

const LangBadge: React.FC<{ lang: string }> = ({ lang }) => (
  <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded ml-1 font-dmsans uppercase">
    {lang}
  </span>
);

// ─── Ensure MultiLangValue ────────────────────────────────────────────────────

const asML = (v: unknown): MultiLangValue => {
  if (!v) return { en: "" };
  if (typeof v === "object" && v !== null) return v as MultiLangValue;
  return { en: v as string };
};

// ─── Safe label resolver ──────────────────────────────────────────────────────
function resolveLabel(label: any, fieldName = ""): string {
  if (!label) return fieldName || "";
  if (typeof label === "string") {
    try { const p = JSON.parse(label); if (typeof p === "object" && p !== null) { const v = p["en"] ?? p["de"] ?? Object.values(p).find((x: any) => x); return v ? String(v) : fieldName; } } catch { }
    return label || fieldName;
  }
  if (typeof label === "object") { const v = (label as any)["en"] ?? (label as any)["de"] ?? Object.values(label as any).find((x: any) => x); return v ? String(v) : fieldName; }
  return String(label) || fieldName;
}

// ─── Email template generators ────────────────────────────────────────────────
function generateApprovalTemplate(fields: FormField[], accentColor = "#22c55e"): string {
  const skip = new Set(["heading", "paragraph", "divider", "spacer", "html"]);
  const rows = fields.filter(f => !skip.has(f.type)).map(f => {
    const lbl = resolveLabel(f.label, f.name);
    return `      <tr>
        <td style="padding:10px 16px;font-weight:600;color:#374151;background:#f9fafb;width:38%;border-bottom:1px solid #e5e7eb;font-size:13px;">${lbl}</td>
        <td style="padding:10px 16px;color:#111827;border-bottom:1px solid #e5e7eb;font-size:13px;">{${f.name}}</td>
      </tr>`;
  }).join("\n");

  return `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f0fdf4;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f0fdf4;padding:32px 16px;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">

        <!-- HEADER -->
        <tr>
          <td style="background:${accentColor};padding:28px 32px;text-align:center;">
            <div style="width:56px;height:56px;background:rgba(255,255,255,0.2);border-radius:50%;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;font-size:28px;">✅</div>
            <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:700;">Your submission is approved!</h1>
          </td>
        </tr>

        <!-- GREETING -->
        <tr>
          <td style="padding:28px 32px 8px;">
            <p style="margin:0 0 12px;font-size:15px;color:#111827;">Hi <strong>{submitter_name}</strong>,</p>
            <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.6;">
              Great news! Your submission <strong>#{submission_id}</strong> has been reviewed and <strong style="color:${accentColor};">approved</strong>.
            </p>
            <!-- Admin note (shown only when note is provided) -->
            <div style="background:#f0fdf4;border-left:4px solid ${accentColor};padding:12px 16px;border-radius:0 8px 8px 0;margin-bottom:12px;">
              {approval_note}
            </div>
          </td>
        </tr>

        <!-- SUBMISSION DETAILS -->
        <tr>
          <td style="padding:8px 32px 8px;">
            <p style="margin:0 0 12px;font-size:13px;font-weight:600;color:#374151;text-transform:uppercase;letter-spacing:0.05em;">Your Submission</p>
            <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;">
${rows}
            </table>
          </td>
        </tr>

        <!-- FOOTER -->
        <tr>
          <td style="background:#f9fafb;padding:20px 32px;border-top:1px solid #e5e7eb;text-align:center;margin-top:16px;">
            <p style="margin:0 0 4px;font-size:12px;color:#6b7280;">Submitted on {submitted_at}</p>
            <p style="margin:0;font-size:11px;color:#9ca3af;">Sent by {form_name} · Powered by Mobimint</p>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

function generateRejectionTemplate(fields: FormField[], accentColor = "#ef4444"): string {
  const skip = new Set(["heading", "paragraph", "divider", "spacer", "html"]);
  const rows = fields.filter(f => !skip.has(f.type)).map(f => {
    const lbl = resolveLabel(f.label, f.name);
    return `      <tr>
        <td style="padding:10px 16px;font-weight:600;color:#374151;background:#f9fafb;width:38%;border-bottom:1px solid #e5e7eb;font-size:13px;">${lbl}</td>
        <td style="padding:10px 16px;color:#111827;border-bottom:1px solid #e5e7eb;font-size:13px;">{${f.name}}</td>
      </tr>`;
  }).join("\n");

  return `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#fff1f2;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#fff1f2;padding:32px 16px;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">

        <!-- HEADER -->
        <tr>
          <td style="background:${accentColor};padding:28px 32px;text-align:center;">
            <div style="width:56px;height:56px;background:rgba(255,255,255,0.2);border-radius:50%;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;font-size:28px;">❌</div>
            <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:700;">Update on your submission</h1>
          </td>
        </tr>

        <!-- GREETING -->
        <tr>
          <td style="padding:28px 32px 8px;">
            <p style="margin:0 0 12px;font-size:15px;color:#111827;">Hi <strong>{submitter_name}</strong>,</p>
            <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.6;">
              Thank you for submitting. After review, we are unable to process your submission <strong>#{submission_id}</strong> at this time.
            </p>
            <!-- Admin note (shown only when note is provided) -->
            <div style="background:#fff1f2;border-left:4px solid ${accentColor};padding:12px 16px;border-radius:0 8px 8px 0;margin-bottom:12px;">
              {rejection_note}
            </div>
            <p style="font-size:13px;color:#6b7280;line-height:1.6;">If you have any questions, please reply to this email.</p>
          </td>
        </tr>

        <!-- SUBMISSION DETAILS -->
        <tr>
          <td style="padding:8px 32px 8px;">
            <p style="margin:0 0 12px;font-size:13px;font-weight:600;color:#374151;text-transform:uppercase;letter-spacing:0.05em;">Your Submission</p>
            <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;">
${rows}
            </table>
          </td>
        </tr>

        <!-- FOOTER -->
        <tr>
          <td style="background:#f9fafb;padding:20px 32px;border-top:1px solid #e5e7eb;text-align:center;margin-top:16px;">
            <p style="margin:0 0 4px;font-size:12px;color:#6b7280;">Submitted on {submitted_at}</p>
            <p style="margin:0;font-size:11px;color:#9ca3af;">Sent by {form_name} · Powered by Mobimint</p>
          </td>
        </tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// ─── Variable chip (click to insert) ────────────────────────────────────────
const VarChip: React.FC<{
  token: string;
  desc: string;
  color?: "purple" | "green" | "red" | "blue";
  onInsert: (t: string) => void;
}> = ({ token, desc, color = "purple", onInsert }) => {
  const [copied, setCopied] = React.useState(false);
  const cls = {
    purple: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 hover:border-purple-300",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300",
    red: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-300",
    blue: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300",
  }[color];
  return (
    <button
      type="button"
      title={desc}
      onClick={() => { onInsert(token); setCopied(true); setTimeout(() => setCopied(false), 1000); }}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-b-2 text-[11px] font-mono font-semibold transition-all duration-200 active:scale-95 active:border-b ${cls}`}
    >
      {copied ? <Check className="h-3 w-3" /> : null}
      {token}
      <span className="font-sans font-medium opacity-60 text-[10px] ml-0.5 bg-white/50 px-1.5 py-0.5 rounded-md">{desc}</span>
    </button>
  );
};

// ─── Per-language mini tabs (compact, for inside cards) ───────────────────────

const MiniLangTabs: React.FC<{
  languages: Language[];
  active: string;
  onChange: (code: string) => void;
}> = ({ languages, active, onChange }) => {
  if (languages.length <= 1) return null;
  return (
    <div className="flex items-center gap-1 bg-backgroundLightGray border border-borderGreyColor rounded-lg px-1 py-0.5">
      <Globe className="h-3 w-3 text-iconTextGreyColor flex-shrink-0" />
      {languages.map(l => (
        <button
          key={l.code}
          type="button"
          onClick={() => onChange(l.code)}
          className={`px-1.5 py-0.5 rounded text-[10px] font-dmsans font-medium transition-colors ${active === l.code ? "bg-primary text-white" : "text-iconTextGreyColor hover:text-blackDark"
            }`}
        >
          {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

// ─── Multi-language template editor ─────────────────────────────────────────

const APPROVAL_VARS = [
  { token: "{submitter_name}", desc: "Submitter name", color: "purple" as const },
  { token: "{submitter_email}", desc: "Submitter email", color: "purple" as const },
  { token: "{form_name}", desc: "Form name", color: "purple" as const },
  { token: "{submission_id}", desc: "Submission ID", color: "purple" as const },
  { token: "{submitted_at}", desc: "Submission date", color: "purple" as const },
  { token: "{approval_note}", desc: "Admin note (approval)", color: "green" as const },
  { token: "{rejection_note}", desc: "Admin note (rejection)", color: "red" as const },
];

const TemplateEditor: React.FC<{
  label: string;
  icon: React.ReactNode;
  iconBg: string;
  isApproval: boolean;
  subject: MultiLangValue;
  body: MultiLangValue;
  fields: FormField[];
  activeLang: string;
  languages: Language[];
  accentColor: string;
  onSubject: (v: MultiLangValue) => void;
  onBody: (v: MultiLangValue) => void;
}> = ({ label, icon, iconBg, isApproval, subject, body, fields, activeLang, languages, accentColor, onSubject, onBody }) => {
  const [editorLang, setEditorLang] = useState(activeLang);
  const [mode, setMode] = useState<"html" | "preview">("html");
  const [showVars, setShowVars] = useState(true);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => { setEditorLang(activeLang); }, [activeLang]);

  const subjectVal = mlGet(subject, editorLang);
  const bodyVal = mlGet(body, editorLang);

  const handleSubject = (text: string) => onSubject(mlSet(subject, editorLang, text));
  const handleBody = (text: string) => onBody(mlSet(body, editorLang, text));

  const insertIntoBody = useCallback((token: string) => {
    const ta = bodyRef.current;
    const cur = mlGet(body, editorLang);
    if (ta) {
      const s = ta.selectionStart ?? cur.length;
      const e = ta.selectionEnd ?? cur.length;
      const next = cur.slice(0, s) + token + cur.slice(e);
      onBody(mlSet(body, editorLang, next));
      requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(s + token.length, s + token.length); });
    } else {
      onBody(mlSet(body, editorLang, cur + token));
    }
  }, [body, editorLang, onBody]);

  const insertIntoSubject = useCallback((token: string) => {
    const inp = subjRef.current;
    const cur = mlGet(subject, editorLang);
    if (inp) {
      const s = inp.selectionStart ?? cur.length;
      const e = inp.selectionEnd ?? cur.length;
      const next = cur.slice(0, s) + token + cur.slice(e);
      onSubject(mlSet(subject, editorLang, next));
      requestAnimationFrame(() => { inp.focus(); inp.setSelectionRange(s + token.length, s + token.length); });
    } else {
      onSubject(mlSet(subject, editorLang, cur + token));
    }
  }, [subject, editorLang, onSubject]);

  const skip = new Set(["heading", "paragraph", "divider", "spacer", "html"]);
  const fieldVars = fields.filter(f => !skip.has(f.type) && f.name).map(f => ({
    token: `{${f.name}}`,
    desc: resolveLabel(f.label, f.name),
    color: "blue" as const,
  }));

  const missingLangs = languages.filter(l => {
    if (l.code === "en") return false;
    return !(subject[l.code] ?? "").trim() || !(body[l.code] ?? "").trim();
  });

  return (
    <div className="bg-white border border-gray-200/80 rounded-2xl overflow-hidden mb-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)] transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className={`flex items-center gap-3 px-6 py-4 border-b border-gray-100 ${iconBg.replace('bg-green-50', 'bg-emerald-50/50').replace('bg-red-50', 'bg-red-50/50')}`}>
        <div className={`p-1.5 rounded-lg ${iconBg.replace('bg-green-50', 'bg-emerald-100/50 text-emerald-600').replace('bg-red-50', 'bg-red-100/50 text-red-600')}`}>
          {icon}
        </div>
        <p className="text-[15px] font-semibold text-gray-900 font-sans flex-1 tracking-tight">{label}</p>
        <MiniLangTabs languages={languages} active={editorLang} onChange={setEditorLang} />
      </div>

      <div className="p-6 space-y-6">
        {/* Missing translations */}
        {missingLangs.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {missingLangs.map(l => (
              <button key={l.code} type="button" onClick={() => setEditorLang(l.code)}
                className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 px-2 py-1 rounded-lg font-sans font-medium hover:bg-orange-100 transition-colors flex items-center gap-1.5 shadow-sm">
                ⚠ {l.code.toUpperCase()} not translated — click to add
              </button>
            ))}
          </div>
        )}

        {/* Variables panel */}
        <div className="border border-gray-200/80 rounded-2xl overflow-hidden shadow-[0_2px_10px_rgba(0,0,0,0.02)] bg-white">
          <button type="button" onClick={() => setShowVars(v => !v)}
            className="w-full flex items-center justify-between px-5 py-4 bg-gray-50/80 text-[13px] font-semibold text-gray-900 font-sans hover:bg-gray-100/80 transition-colors">
            <span className="flex items-center gap-2.5">
              <div className="p-1.5 bg-blue-100 rounded-lg"><Code className="h-4 w-4 text-blue-600" /></div>
              Template Variables
              <span className="bg-gray-200/80 text-gray-600 px-2 py-0.5 rounded-full text-[10px] font-bold ml-1">{APPROVAL_VARS.length + fieldVars.length}</span>
            </span>
            <div className="flex items-center gap-2 text-gray-400">
              <span className="text-[11px] font-medium hidden sm:inline-block">Click to insert</span>
              {showVars ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </button>
          <div className={`transition-all duration-300 ease-in-out ${showVars ? 'opacity-100 max-h-[800px] border-t border-gray-100' : 'opacity-0 max-h-0'}`}>
            <div className="p-5 space-y-5 bg-white">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider font-sans mb-3 flex items-center gap-1.5"><Zap className="h-3 w-3 text-purple-500" /> System</p>
                <div className="flex flex-wrap gap-2">
                  {APPROVAL_VARS.map(v => (
                    <VarChip key={v.token} token={v.token} desc={v.desc} color={v.color} onInsert={insertIntoBody} />
                  ))}
                </div>
              </div>
              {fieldVars.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider font-sans mb-3 flex items-center gap-1.5"><Zap className="h-3 w-3 text-blue-500" /> Form Fields</p>
                  <div className="flex flex-wrap gap-2">
                    {fieldVars.map(v => (
                      <VarChip key={v.token} token={v.token} desc={v.desc} color="blue" onInsert={insertIntoBody} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subject */}
        <div className="bg-white border border-gray-200/80 rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-[#ed5a29]/10 rounded-lg">
              <Zap className="h-4 w-4 text-[#ed5a29]" />
            </div>
            <Lbl className="!mb-0">Subject Line {languages.length > 1 && <span className="text-[9px] text-white bg-[#ed5a29] px-1.5 py-0.5 rounded ml-2 uppercase font-bold">{editorLang}</span>}</Lbl>
          </div>
          <Inp ref={subjRef} value={subjectVal} onChange={e => handleSubject(e.target.value)}
            placeholder={isApproval ? "Your submission has been approved ✓" : "Update on your submission"} className="text-[13px] font-medium" />
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider self-center mr-1">Quick Insert:</span>
            {[...APPROVAL_VARS.slice(0, 3), ...fieldVars.slice(0, 3)].map(v => (
              <VarChip key={v.token} token={v.token} desc={v.desc} color={v.color ?? "blue"} onInsert={insertIntoSubject} />
            ))}
          </div>
        </div>

        {/* Body editor */}
        <div className="border border-gray-200/80 rounded-2xl overflow-hidden shadow-[0_2px_10px_rgba(0,0,0,0.02)] bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-[#1e1e1e] text-white">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5 opacity-50">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
              </div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 font-mono ml-2">Email.html</span>
              {languages.length > 1 && <span className="text-[9px] text-white bg-[#ed5a29] px-1.5 py-0.5 rounded ml-1 uppercase font-bold font-sans">{editorLang}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => {
                const tmpl = isApproval
                  ? generateApprovalTemplate(fields, accentColor)
                  : generateRejectionTemplate(fields, accentColor);
                onBody(mlSet(body, editorLang, tmpl));
              }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold font-sans bg-gradient-to-r from-[#ed5a29] to-[#f47b54] text-white rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 active:scale-95">
                <Zap className="h-3 w-3" /> Auto Generate
              </button>
              <div className="flex bg-[#2d2d2d] rounded-lg p-0.5 shadow-inner">
                <button type="button" onClick={() => setMode("html")}
                  className={`flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold font-sans rounded-md transition-all duration-200 ${mode === "html" ? "bg-[#404040] text-white shadow-sm" : "text-gray-400 hover:text-white hover:bg-[#333]"}`}>
                  <Code className="h-3 w-3" /> Code
                </button>
                <button type="button" onClick={() => setMode("preview")}
                  className={`flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold font-sans rounded-md transition-all duration-200 ${mode === "preview" ? "bg-[#404040] text-white shadow-sm" : "text-gray-400 hover:text-white hover:bg-[#333]"}`}>
                  <Eye className="h-3 w-3" /> Preview
                </button>
              </div>
            </div>
          </div>

          {mode === "html" ? (
            <div className="relative bg-[#1e1e1e]">
              <div className="absolute left-0 top-0 bottom-0 w-10 border-r border-[#333] bg-[#1e1e1e] z-0 flex flex-col pt-3 pb-3 text-right pr-2 select-none font-mono text-[11px] text-gray-600">
                {Array.from({ length: 14 }).map((_, i) => <div key={i} className="leading-[1.6]">{i + 1}</div>)}
              </div>
              <Txt ref={bodyRef} mono rows={14} value={bodyVal} onChange={e => handleBody(e.target.value)}
                placeholder="<!-- Click Auto Generate for a beautiful template, or write your own HTML. -->"
                className="!text-[12px] !bg-transparent !text-gray-100 !border-0 focus:!ring-0 !rounded-none w-full !pl-12 !pt-3 !pb-3 relative z-10 leading-[1.6] selection:bg-[#ed5a29]/30 placeholder:text-gray-600 !shadow-none" />
            </div>
          ) : (
            <div className="bg-gray-50 border-t border-gray-200">
              <div className="flex justify-center p-3 border-b border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full shadow-inner">
                  <Globe className="h-3 w-3 text-gray-400" />
                  <span className="text-[10px] font-mono text-gray-500 font-semibold tracking-wide">Live Preview ({editorLang.toUpperCase()})</span>
                </div>
              </div>
              <div className="p-4 sm:p-8 flex justify-center">
                <div className="w-full max-w-[600px] bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden border border-gray-100">
                  {bodyVal ? (
                    <iframe
                      srcDoc={bodyVal.replace(/\{([^}]+)\}/g, (_, k) => `<span style="background:rgba(237,90,41,0.1);color:#ed5a29;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:700;font-family:monospace;border:1px solid rgba(237,90,41,0.2)">{${k}}</span>`)}
                      className="w-full border-0" style={{ height: 600 }}
                      title="Email preview" sandbox="allow-same-origin"
                    />
                  ) : (
                    <div className="h-48 flex items-center justify-center text-sm text-gray-400 font-sans font-medium">
                      Click Auto Generate to see a preview
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Translation status pills */}
          {languages.length > 1 && (
            <div className="flex flex-wrap gap-2 p-4 border-t border-gray-200 bg-gray-50/50">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider self-center">Translations:</span>
              {languages.map(l => {
                const done = (subject[l.code] ?? "").trim().length > 0 && (body[l.code] ?? "").trim().length > 0;
                return (
                  <button key={l.code} type="button" onClick={() => setEditorLang(l.code)}
                    className={`text-[10px] px-2.5 py-1 rounded-full font-bold transition-colors border shadow-sm ${editorLang === l.code ? "bg-[#ed5a29] text-white border-[#ed5a29]"
                        : done ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                          : "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"
                      }`}>
                    {done ? "✓" : "⚠"} {l.code.toUpperCase()}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── APPROVAL TAB (main export) ───────────────────────────────────────────────

export const ApprovalTab: React.FC<{
  form: Form;
  onChange: (f: Form) => void;
  languages?: Language[];
  activeLang?: string;
}> = ({ form, onChange, languages = DEFAULT_LANGUAGES, activeLang = "en" }) => {

  const set = (k: keyof Form, v: any) => onChange({ ...form, [k]: v });

  const enabled = !!(form as any).approval_enabled;

  // Coerce all four template fields to MultiLangValue
  const approvalSubject = asML((form as any).approval_email_subject);
  const approvalBody = asML((form as any).approval_email_body);
  const rejectionSubject = asML((form as any).rejection_email_subject);
  const rejectionBody = asML((form as any).rejection_email_body);

  return (
    <>
      {/* ── Master toggle card ── */}
      <Card
        title="Submission Approval"
        sub="Require admin approval before notifying or processing a submission. When enabled, each submission shows Approve / Reject buttons."
      >
        <Toggle
          checked={enabled}
          onChange={v => set("approval_enabled" as any, v)}
          label="Enable approval workflow"
          sub="Admin must approve or reject each submission from the Submissions view"
        />

        {enabled && (
          <>
            <div className="mt-4 pt-4 border-t border-borderGreyColor space-y-0">
              <Toggle
                checked={!!(form as any).approval_send_approved_email}
                onChange={v => set("approval_send_approved_email" as any, v)}
                label="Send email on approval"
                sub="Email the submitter when admin clicks Approve"
              />
              <Toggle
                checked={!!(form as any).approval_send_rejected_email}
                onChange={v => set("approval_send_rejected_email" as any, v)}
                label="Send email on rejection"
                sub="Email the submitter when admin clicks Reject"
              />
              <Toggle
                checked={!!(form as any).approval_require_note}
                onChange={v => set("approval_require_note" as any, v)}
                label="Require admin note"
                sub="Force admin to enter a note before approving or rejecting"
              />
            </div>

            <G2>
              {/* Email field selector — labels shown in activeLang */}
              <Fld label="Submitter email field">
                <select
                  value={(form as any).approval_email_field ?? ""}
                  onChange={e => set("approval_email_field" as any, e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-borderGreyColor rounded-lg bg-white font-dmsans focus:border-primary outline-none"
                >
                  <option value="">— auto-detect email field —</option>
                  {form.fields
                    .filter(f => f.type === "email" || f.name.includes("email"))
                    .map(f => (
                      <option key={f._id ?? f.name} value={f.name}>
                        {mlGet(f.label, activeLang) || mlGet(f.label, "en") || f.name}
                      </option>
                    ))}
                </select>
              </Fld>

              {/* From name — plain string, not translated */}
              <Fld label="From Name">
                <Inp
                  value={(form as any).approval_from_name ?? ""}
                  onChange={e => set("approval_from_name" as any, e.target.value)}
                  placeholder="e.g. Support Team"
                />
              </Fld>
            </G2>

            {/* Multi-language notice */}
            {languages.length > 1 && (
              <InfoBox type="info">
                <Globe className="inline h-3 w-3 mr-1 mb-0.5" />
                Your store has <strong>{languages.length} languages</strong>.
                Each email template below has a per-language editor — use the language tabs inside each card to add translations.
                The backend will send the email in the submitter's language if a translation exists, otherwise falls back to English.
              </InfoBox>
            )}
          </>
        )}
      </Card>

      {/* ── Approval email template ── */}
      {enabled && !!(form as any).approval_send_approved_email && (
        <TemplateEditor
          label="Approval Email Template"
          icon={<CheckCircle className="h-4 w-4 text-green-600" />}
          iconBg="bg-green-50"
          isApproval={true}
          fields={form.fields}
          activeLang={activeLang}
          languages={languages}
          accentColor="#22c55e"
          subject={approvalSubject}
          body={approvalBody}
          onSubject={v => onChange({ ...form, approval_email_subject: v } as any)}
          onBody={v => onChange({ ...form, approval_email_body: v } as any)}
        />
      )}

      {/* ── Rejection email template ── */}
      {enabled && !!(form as any).approval_send_rejected_email && (
        <TemplateEditor
          label="Rejection Email Template"
          icon={<XCircle className="h-4 w-4 text-red-500" />}
          iconBg="bg-red-50"
          isApproval={false}
          fields={form.fields}
          activeLang={activeLang}
          languages={languages}
          accentColor="#ef4444"
          subject={rejectionSubject}
          body={rejectionBody}
          onSubject={v => onChange({ ...form, rejection_email_subject: v } as any)}
          onBody={v => onChange({ ...form, rejection_email_body: v } as any)}
        />
      )}

      {/* Hint when no templates are active yet */}
      {enabled && !(form as any).approval_send_approved_email && !(form as any).approval_send_rejected_email && (
        <InfoBox type="warn">
          Enable "Send email on approval" or "Send email on rejection" above to configure email templates.
        </InfoBox>
      )}

      {/* ── How it works ── */}
      {enabled && (
        <Card title="How it works">
          <ol className="list-decimal list-inside space-y-2 text-xs text-iconTextGreyColor font-dmsans">
            <li>User submits the form — submission saved with status <strong className="text-blackDark">pending</strong>.</li>
            <li>Admin opens <strong className="text-blackDark">Submissions</strong> and sees <strong className="text-blackDark">Approve / Reject</strong> buttons.</li>
            <li>Admin optionally enters a note, then clicks Approve or Reject.</li>
            <li>Status updates and the configured email is sent to the submitter
              {languages.length > 1 && <> in their language if translated, otherwise English</>}.
            </li>
          </ol>
        </Card>
      )}
    </>
  );
};
